"use client";

import { useRouter } from "next/navigation";
import React, { Fragment, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dialog, Divider, Modal } from "staysure-component-library";

import { ApiError } from "@/app/common-types/api-error.types";
import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import RichText from "@/components/atoms/rich-text/rich-text";
import RemoveModal from "@/components/molecules/quote-section/acos/remove-modal";
import withApplication from "@/components/with-application";
import { RenewalPage } from "@/config/app-constant";
import { PRODUCT } from "@/constants";
import useUpdateAcoData from "@/hooks/api/quote/use-update-aco";
import { useRenewalStateContext } from "@/providers/renewal-state-provider";
import { PolicyExtrasAccordionType } from "@/schema/renewal-page/middle-panel.schema";
import { RootState } from "@/store/root-reducer";
import { setApiError } from "@/store/slices/errors/api-error.slice";
import { updateQuote } from "@/store/slices/quote/quote.slice";
import { OptionalExtra } from "@/types/OptionalExtraType";
import { apiErrorMappings } from "@/utils/errors/api-error-map";
import handleError, { ErrorResponse } from "@/utils/handle-errors";

import CoverOption from "./cover-option/cover-option";
import Styles from "./policy-extras.module.css";

type PolicyExtrasContentType = {
  policyExtrasAccordionCMSData: PolicyExtrasAccordionType;
};

export const GOLF_YEAR_PRICE = {
  avanti: 11.28,
  staysure: 32.95,
} as const;

const brand = (PRODUCT as "staysure" | "avanti") ?? "staysure";
const golfYearPrice = GOLF_YEAR_PRICE[brand];

function PolicyExtrasContent({
  policyExtrasAccordionCMSData,
  application,
}: Readonly<PolicyExtrasContentType & { application?: string }>) {
  const [isRemoveModalOpen, setIsRemoveModalOpen] = React.useState(false);
  const [removeAcoCode, setRemoveAcoCode] = React.useState("");
  const [removeAcoTitle, setRemoveAcoTitle] = React.useState("");

  const { attributes } = useSelector((state: RootState) => state.quote);
  const { priceResult } = attributes;

  const router = useRouter();
  const dispatch = useDispatch();

  const { data, loading, error, updateAcoData } = useUpdateAcoData();
  const { updateLastEditAccordion } = useRenewalStateContext();

  const acoItems = Object.values(policyExtrasAccordionCMSData.acoItems).map(
    (item) => item.acoItem
  );

  const optionalExtras = priceResult?.optionalExtras.map(
    (extra: OptionalExtra) => {
      const cmsData = acoItems.find((acoItem) => acoItem.acoId === extra.code);

      const extraData = extra.price[0]; // Use the first price option

      return {
        code: extra.code,
        title: cmsData?.acoTitle ?? extraData.name,
        description: cmsData?.description ?? `Cover for ${extraData.name}.`,
        sellPrice: extraData.sellPrice,
        pclPrice: extraData.pclPrice,
        pclTotalCost: extraData.pclTotalCost,
        selected: extraData.selected,
        state: extraData.state,
        viewAddLabel:
          policyExtrasAccordionCMSData.viewAddButtonLabel || "View/add",
        removeLabel: policyExtrasAccordionCMSData.removeButtonLabel || "Remove",
        includedLabel: policyExtrasAccordionCMSData.includedLabel || "Included",
        notIncludedLabel:
          policyExtrasAccordionCMSData.notIncludedLabel || "Not included",
      };
    }
  );

  const optionsForPolicyExtras = optionalExtras?.filter(
    (extra) =>
      ["ENABLED", "INCLUDED"].some((state) => extra.state.includes(state)) &&
      extra.selected
  );
  const optionsForTailorYourExtras = optionalExtras?.filter(
    (extra) => extra.state.includes("ENABLED") && !extra.selected
  );

  const hasEnabledOptions = optionalExtras?.some((extra) =>
    extra.state.includes("ENABLED")
  );

  const handleRemoveModal = (extraCode: string, extraTitle: string) => {
    setRemoveAcoCode(extraCode);
    setRemoveAcoTitle(extraTitle);
    setIsRemoveModalOpen(true);
  };

  const removeAco = () => {
    const updatedExtrasArray = priceResult?.optionalExtras.map((extra) => {
      if (extra.code === removeAcoCode) {
        return {
          ...extra,
          price: extra.price.map((priceOption) => ({
            ...priceOption,
            selected: false,
          })),
        };
      }
      return extra;
    });

    if (updatedExtrasArray) {
      updateAcoData(updatedExtrasArray);
    }
  };

  useEffect(() => {
    const handleEffect = async () => {
      if (!loading && data && !error) {
        dispatch(updateQuote(data));
      } else if (!loading && error !== null) {
        if (application === RenewalPage) {
          await handleError(error as ApiError<ErrorResponse>, router, dispatch);
        }
        if (error.response?.status === 401) {
          router.push("/");
        } else {
          const errorObject = await apiErrorMappings(error);
          errorObject.multiButton = true;
          dispatch(setApiError(errorObject));
        }
      }
    };
    handleEffect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loading, error, dispatch, router]);

  return (
    <>
      {isRemoveModalOpen && (
        <Modal
          onOpenChange={(e: boolean) => setIsRemoveModalOpen(e)}
          isOpen={isRemoveModalOpen}
          modalStyles={{
            top: `50%`,
            left: `50%`,
            transform: `translate(-50%, -50%)`,
          }}
          className={`bg-surface-neutral-option-1 md:max-w-[416px] max-w-[312px] relative rounded-radius-lg `}
          uderlayClassName="z-[100] bg-interactive-overlay-default"
        >
          <Dialog className=" relative">
            <RemoveModal
              title={policyExtrasAccordionCMSData.removeModalTitle}
              onSelect={(e) => {
                setIsRemoveModalOpen(false);
                if (!e) {
                  removeAco();
                }
              }}
              description={`${policyExtrasAccordionCMSData.removeModalContent} ${removeAcoTitle}`}
              confirmCTALabel={
                policyExtrasAccordionCMSData.removeModalConfirmCTALabel
              }
              cancelCTALabel={
                policyExtrasAccordionCMSData.removeModalCancelCTALabel
              }
            />
          </Dialog>
        </Modal>
      )}

      {/* Enabled, Included Options */}
      {optionsForPolicyExtras?.map((extra, index) => (
        <Fragment key={extra.code}>
          <CoverOption
            code={extra.code}
            title={extra.title}
            fullPrice={`£${extra.sellPrice.toFixed(2)}`}
            monthlyPrice={extra.pclPrice ? `£${extra.pclPrice}` : ""}
            totalPrice={extra.pclTotalCost ? `£${extra.pclTotalCost}` : ""}
            description={extra.description}
            state={extra.includedLabel}
            actionLabel={extra.removeLabel}
            action={handleRemoveModal}
          />
          {(index < optionsForPolicyExtras.length - 1 ||
            (index === optionsForPolicyExtras.length - 1 &&
              optionsForTailorYourExtras &&
              optionsForTailorYourExtras.length > 0)) && (
            <Divider margin={12} color="#DCDCDC" />
          )}
        </Fragment>
      ))}

      {optionsForTailorYourExtras && optionsForTailorYourExtras.length > 0 ? (
        <>
          {/* Enabled, Not Included Options */}
          {optionsForPolicyExtras && optionsForPolicyExtras.length > 0 && (
            <div className="flex md:max-w-[492px] pt-spacing-xl justify-center items-center gap-2 mb-3">
              <div className="flex-grow flex-shrink-0 basis-0">
                <h2 className="text-text-title font-header-font sm:text-m md:text-l lg:text-m leading-7">
                  {policyExtrasAccordionCMSData.additionalExtrasTitle}
                </h2>
              </div>
            </div>
          )}

          <div>
            {optionsForTailorYourExtras?.map((extra, index) => {
              const isGolfEquipment = extra.code === "GOLF_EQUIPMENT";

              return (
                <Fragment key={extra.code}>
                  <CoverOption
                    code={extra.code}
                    title={extra.title}
                    fullPrice={
                      isGolfEquipment
                        ? `£${golfYearPrice.toFixed(2)}`
                        : `£${extra.sellPrice.toFixed(2)}`
                    }
                    monthlyPrice={extra.pclPrice ? `£${extra.pclPrice}` : ""}
                    totalPrice={
                      extra.pclTotalCost ? `£${extra.pclTotalCost}` : ""
                    }
                    description={extra.description}
                    state={extra.notIncludedLabel}
                    isGolfEquipment={isGolfEquipment}
                    actionLabel={extra.viewAddLabel}
                    action={() => {
                      updateLastEditAccordion("policy-extras");
                      router.push("/policy-extras");
                    }}
                  />
                  {index < optionsForTailorYourExtras.length - 1 && (
                    <Divider margin={12} color="#DCDCDC" />
                  )}
                </Fragment>
              );
            })}
          </div>
        </>
      ) : (
        !hasEnabledOptions && (
          <div className=" flex flex-col items-start gap-spacing-s self-stretch">
            <div className="flex flex-col items-start self-stretch">
              <div className="flex flex-col items-start pb-spacing-m lg:pb-spacing-xs self-stretch">
                <RichText
                  richText={
                    policyExtrasAccordionCMSData.noACOsAvailableHighlightedContent
                  }
                  className={`text-text-title font-body-font text-2xs font-bold leading-6 self-stretch ${Styles.policy_extras_styles}`}
                />
              </div>
              <div className="flex flex-col items-start gap-spacing-xs self-stretch">
                <div className="flex justify-end items-start self-stretch">
                  <LabelButtonWrapper
                    label={
                      policyExtrasAccordionCMSData.seeOtherCoverLevelsLabel
                    }
                    hierarchy="tertiary"
                    className="flex justify-center items-center text-interactive-secondary-default font-body-font text-xs font-semibold leading-[18px] relative top-[-3px] !px-spacing-xxs"
                    onClick={() => {
                      updateLastEditAccordion("policy-extras");
                      router.push("/cover-level");
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )
      )}
    </>
  );
}

export default withApplication(PolicyExtrasContent);
